// Shared by Cloud Functions and the frontend (imported there as @shared).
// Keep this file free of Node- or browser-only APIs.

export const positions = [
  {short: "PG", long: "Point Guard", id: "pg", num: 1},
  {short: "SG", long: "Shooting Guard", id: "sg", num: 2},
  {short: "SF", long: "Small Forward", id: "sf", num: 3},
  {short: "PF", long: "Power Forward", id: "pf", num: 4},
  {short: "C", long: "Center", id: "c", num: 5},
];

/**
 * Looks up a position by its number (1 = PG ... 5 = C).
 * @param {number} num
 * @return {object|undefined}
 */
export function positionByNum(num) {
  return positions.find((p) => p.num === Number(num));
}

// How well a player plays in a lineup slot away from their position: full
// strength at their main position, nearly full at their listed second
// position, and less the further the slot is from their main position,
// with the cost growing faster than the distance (a guard at small forward
// is a stretch; a point guard at center is a mismatch).
export const SECOND_POSITION_FACTOR = 0.98;
const STEP_FACTORS = [1, 0.97, 0.92, 0.85, 0.76];

/**
 * The share of their ratings a player brings to a lineup slot.
 * @param {object} player {pos1, pos2}
 * @param {number} slot position number 1 (PG) to 5 (C)
 * @return {number} 1 at the main position, down to 0.76 four steps away
 */
export function positionFactor(player, slot) {
  if (!slot || !player?.pos1 || slot === player.pos1) return 1;
  if (slot === player.pos2) return SECOND_POSITION_FACTOR;
  return STEP_FACTORS[Math.min(4, Math.abs(slot - player.pos1))];
}

/**
 * The overall a player plays to in a slot (shown in the lineup editor).
 * @param {object} player
 * @param {number} slot
 * @return {number}
 */
export function overallAt(player, slot) {
  return Math.round(player.overall * positionFactor(player, slot));
}

// Every order of the five slots (120), worked out once.
const ORDERS = (() => {
  const out = [];
  const build = (rest, picked) => {
    if (!rest.length) return out.push(picked);
    rest.forEach((x, i) =>
      build([...rest.slice(0, i), ...rest.slice(i + 1)], [...picked, x]));
  };
  build([0, 1, 2, 3, 4], []);
  return out;
})();

/**
 * The five players on the floor in the slots that suit them best (most
 * total overall after positionFactor), as a team would line up: a center
 * listed at point guard just plays center when the real point guard is on
 * the floor too. Ties keep the given order.
 * @param {object[]} five players, one per slot (PG..C)
 * @return {object[]} the same players, reordered by slot
 */
export function bestSlots(five) {
  if (five.length !== 5) return five;
  const value = (order) => order.reduce((n, who, slot) =>
    n + (five[who].overall ?? 50) * positionFactor(five[who], slot + 1), 0);
  let best = ORDERS[0];
  let bestValue = value(best);
  for (const order of ORDERS) {
    const v = value(order);
    if (v > bestValue + 1e-9) {
      best = order;
      bestValue = v;
    }
  }
  return best.map((who) => five[who]);
}
